import { useEffect, useMemo, useState } from "react";
import { JEDI_HR_URL } from "../../constants";
import axiosClient from "../../utils/axiosClient";
import "./Subscriptions.css";

const BASE_URL = "https://" + JEDI_HR_URL + "/subscriptions";
const api = (path, body = {}) => axiosClient.post(`${BASE_URL}/${path}`, body).then((r) => r?.data || {});

// Aurora timestamps arrive without a zone and are UTC
const fmtDate = (d) =>
  d
    ? new Date(/Z|[+-]\d\d:\d\d$/.test(d) ? d : d + "Z").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "-";
const slug = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
const STOP = ["report", "daily", "and", "the", "for", "weekly"];
const tokens = (s) => slug(s).split("_").filter((t) => t.length > 2 && !STOP.includes(t));
const snippetFor = (code) => `SELECT email_id FROM ssp_prod.subscription WHERE report_code = '${code}'`;

const KIND_LABEL = { employee: null, exited: "EXITED", external: "EXTERNAL", mailbox: "MAILBOX" };
const ACTION_LABEL = {
  subscribe: "subscribed",
  unsubscribe: "unsubscribed",
  owner_add: "added",
  owner_remove: "removed",
  exit_remove: "removed (left More)",
  invalid_remove: "removed (address bounces)",
  auto_inactive: "switched the report off - no run seen for 14 days",
  auto_reactive: "switched the report back on - it is running again",
};
const SHOW_EMAIL = ["owner_add", "owner_remove", "exit_remove", "invalid_remove"];
const TABS = [
  { id: "mine", label: "My subscriptions" },
  { id: "owned", label: "Reports I own" },
  { id: "all", label: "All reports" },
];

// ------------------------------------------------------------------ small pieces
const Pill = ({ r }) =>
  !r.active ? (
    <span className="rs-pill rs-pill-off">SWITCHED OFF</span>
  ) : r.visibility === "restricted" ? (
    <span className="rs-pill rs-pill-restricted">RESTRICTED</span>
  ) : (
    <span className="rs-pill rs-pill-open">OPEN</span>
  );

const CatTag = ({ c }) => (c ? <span className="rs-cat">{c}</span> : null);

// last_hit_at is the newest time a sending job read this report's recipient list
const LastRun = ({ r }) =>
  !r.active ? (
    <span className="rs-warn-text">{r.inactive_reason || "Switched off"}</span>
  ) : (
    <span>
      Last sent <b>{r.last_hit_at ? fmtDate(r.last_hit_at) : "not seen yet"}</b>
    </span>
  );

const ReportCard = ({ r, action, pill = true }) => (
  <div className={`rs-card ${r.active ? "" : "rs-card-off"}`}>
    <div className="rs-code-badge">
      <span className="rs-mono">{r.report_code}</span>
    </div>
    <div className="rs-card-main">
      <div className="rs-card-title">
        {r.report_name} <CatTag c={r.category} />
      </div>
      {r.description && <div className="rs-card-desc">{r.description}</div>}
      <div className="rs-card-meta">
        {r.owner_name && (
          <span>
            Owner <b>{r.owner_name}</b>
          </span>
        )}
        {r.cadence && <span>Runs {r.cadence}</span>}
        <span>
          Subscribers <b>{r.subscribers}</b>
        </span>
        <LastRun r={r} />
      </div>
    </div>
    <div className="rs-card-right">
      {pill && <Pill r={r} />}
      {action}
    </div>
  </div>
);

// category filter chips - "All" plus every category that has at least one report in the list shown
const CatChips = ({ rows, value, onChange }) => {
  const counts = rows.reduce((m, r) => {
    const k = r.category || "Other";
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {});
  const cats = Object.keys(counts).sort((a, b) => (a === "Other") - (b === "Other") || a.localeCompare(b));
  if (cats.length < 2) return null;
  return (
    <div className="rs-chips">
      <button className={`rs-chip ${!value ? "rs-chip-on" : ""}`} onClick={() => onChange("")}>
        All <span className="rs-chip-n">{rows.length}</span>
      </button>
      {cats.map((c) => (
        <button key={c} className={`rs-chip ${value === c ? "rs-chip-on" : ""}`} onClick={() => onChange(value === c ? "" : c)}>
          {c} <span className="rs-chip-n">{counts[c]}</span>
        </button>
      ))}
    </div>
  );
};
const inCat = (r, c) => !c || (r.category || "Other") === c;

const CatSelect = ({ cats, value, onChange }) => (
  <select className="rs-field-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
    <option value="">- pick a category -</option>
    {cats.map((c) => (
      <option key={c} value={c}>
        {c}
      </option>
    ))}
  </select>
);

const PAGE = 25;
const Pager = ({ total, page, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE));
  if (pages <= 1) return null;
  const nums = [...new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages))].sort((a, b) => a - b);
  return (
    <div className="rs-pager">
      <span className="rs-muted">
        {(page - 1) * PAGE + 1}-{Math.min(page * PAGE, total)} of {total}
      </span>
      <span className="rs-pager-btns">
        <button className="rs-page-btn" disabled={page === 1} onClick={() => onPage(page - 1)}>
          Prev
        </button>
        {nums.map((n, i) => (
          <span key={n} className="rs-pager-num">
            {i > 0 && nums[i - 1] !== n - 1 && <span className="rs-muted">...</span>}
            <button className={`rs-page-btn ${n === page ? "rs-page-on" : ""}`} onClick={() => onPage(n)}>
              {n}
            </button>
          </span>
        ))}
        <button className="rs-page-btn" disabled={page === pages} onClick={() => onPage(page + 1)}>
          Next
        </button>
      </span>
    </div>
  );
};

// ------------------------------------------------------------------ add-subscription modal
const AddModal = ({ cat, busy, onSubscribe, onClose }) => {
  const [q, setQ] = useState("");
  const [c, setC] = useState("");
  const t = q.trim().toLowerCase();
  const pool = cat.filter((r) => !r.subscribed && r.active);
  const hits = pool
    .filter((r) => inCat(r, c))
    .filter((r) => !t || r.report_code.toLowerCase().includes(t) || (r.report_name || "").toLowerCase().includes(t) || (r.description || "").toLowerCase().includes(t))
    .slice(0, 40);
  return (
    <div className="rs-modal-overlay" onClick={onClose}>
      <div className="rs-modal rs-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="rs-modal-title">Add a subscription</div>
        <div className="rs-modal-sub">Search by report code or description. The report will come to your company e-mail from its next run.</div>
        <input className="rs-field-input" autoFocus placeholder="e.g. dos, swoos, fill rate" value={q} onChange={(e) => setQ(e.target.value)} />
        <CatChips rows={pool} value={c} onChange={setC} />
        <div className="rs-pick-list">
          {hits.map((r) => (
            <div key={r.report_code} className="rs-pick-row">
              <span className="rs-mono rs-pick-code">{r.report_code}</span>
              <span className="rs-pick-main">
                <span className="rs-pick-name">
                  {r.report_name} <CatTag c={r.category} />
                </span>
                {r.description && <span className="rs-pick-desc">{r.description}</span>}
              </span>
              <span className="rs-pick-right">
                {r.visibility === "restricted" ? (
                  <span className="rs-ask-owner">ask {r.owner_name ? r.owner_name.split(" ")[0] : "the owner"} to add you</span>
                ) : (
                  <button className="rs-btn-small" disabled={busy === r.report_code} onClick={() => onSubscribe(r)}>
                    {busy === r.report_code ? "Adding..." : "Subscribe"}
                  </button>
                )}
              </span>
            </div>
          ))}
          {hits.length === 0 && <div className="rs-empty-hint">No report matches "{q}".</div>}
        </div>
        <div className="rs-modal-actions">
          <button className="rs-btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

const PeopleDrop = ({ people, onPick }) =>
  people.length > 0 ? (
    <div className="rs-drop">
      {people.map((p) => (
        <div key={p.employee_id} className="rs-drop-opt" onClick={() => onPick(p)}>
          <span>{p.full_name}</span>
          <span className="rs-muted">{p.designation}</span>
          <span className="rs-mono rs-muted">{p.email_id}</span>
        </div>
      ))}
    </div>
  ) : null;

const usePeopleSearch = (q, enabled = true) => {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (!enabled || q.trim().length < 2 || q.includes("@")) {
      setPeople([]);
      return;
    }
    const t = setTimeout(() => api("people_search", { q }).then((res) => setPeople(res.data || [])).catch(() => setPeople([])), 250);
    return () => clearTimeout(t);
  }, [q, enabled]);
  return [people, setPeople];
};

// ------------------------------------------------------------------ owned card
const OwnedCard = ({ r, open, onToggle, onEdit, say, reload }) => {
  const [addQ, setAddQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [people, setPeople] = usePeopleSearch(addQ);
  const snippet = snippetFor(r.report_code);

  const add = (email) => {
    if (adding) return;
    setAdding(true);
    api("add_subscriber", { report_code: r.report_code, email_id: email })
      .then((res) => {
        say(res.status === "success", res.message || res.error_message);
        if (res.status === "success") {
          setAddQ("");
          setPeople([]);
          reload();
        }
      })
      .catch((e) => say(false, e?.message))
      .finally(() => setAdding(false));
  };
  const remove = (email) =>
    api("remove_subscriber", { report_code: r.report_code, email_id: email })
      .then((res) => {
        say(res.status === "success", res.message || res.error_message);
        if (res.status === "success") reload();
      })
      .catch((e) => say(false, e?.message));

  const exited = r.subscribers.filter((s) => s.kind === "exited").length;
  return (
    <div className={`rs-owned ${open ? "rs-owned-open" : ""}`}>
      <div className="rs-owned-row" onClick={onToggle}>
        <div className="rs-code-badge">
          <span className="rs-mono">{r.report_code}</span>
        </div>
        <div className="rs-card-main">
          <div className="rs-card-title">
            {r.report_name} <CatTag c={r.category} />
          </div>
          <div className="rs-card-meta">
            <span>
              <span className="rs-mono">{r.subscribers.length}</span> subscriber{r.subscribers.length === 1 ? "" : "s"}
            </span>
            {exited > 0 && <span className="rs-warn-text">{exited} exited - remove them</span>}
            {r.cadence && <span>Runs {r.cadence}</span>}
            {r.sender_job && <span className="rs-mono rs-muted">{r.sender_job}</span>}
            <LastRun r={r} />
          </div>
        </div>
        <div className="rs-card-right">
          <Pill r={r} />
          <span className="rs-owned-actions">
            <button
              className="rs-link-muted"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(r);
              }}
            >
              Edit
            </button>
            <span className="rs-chevron">{open ? "\u25B4" : "\u25BE"}</span>
          </span>
        </div>
      </div>

      {open && (
        <div className="rs-owned-body">
          <div className="rs-snippet">
            <span className="rs-field-label">Recipients query for the sending script</span>
            <div className="rs-snippet-row">
              <code>{snippet}</code>
              <button
                className="rs-link-muted"
                onClick={() => {
                  navigator.clipboard?.writeText(snippet);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
              >
                {copied ? "copied" : "copy"}
              </button>
            </div>
          </div>

          <div className="rs-add-row">
            <input
              className="rs-field-input"
              placeholder="Add a subscriber - type a name, or paste an e-mail for someone outside More"
              value={addQ}
              onChange={(e) => setAddQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addQ.includes("@") && add(addQ.trim().toLowerCase())}
            />
            {addQ.includes("@") && (
              <button className="rs-btn-small" disabled={adding} onClick={() => add(addQ.trim().toLowerCase())}>
                {adding ? "Adding..." : "Add"}
              </button>
            )}
            <PeopleDrop people={people} onPick={(p) => add(p.email_id)} />
          </div>

          <table className="rs-table rs-table-sub">
            <tbody>
              {r.subscribers.map((s) => (
                <tr key={s.email_id} className={s.kind === "exited" ? "rs-row-off" : ""}>
                  <td>{s.full_name || <span className="rs-muted">-</span>}</td>
                  <td className="rs-mono">{s.email_id}</td>
                  <td className="rs-muted">{s.designation || ""}</td>
                  <td>{KIND_LABEL[s.kind] && <span className={`rs-tag rs-tag-${s.kind}`}>{KIND_LABEL[s.kind]}</span>}</td>
                  <td className="rs-muted">{fmtDate(s.create_at)}</td>
                  <td>
                    <button className="rs-link-danger" onClick={() => remove(s.email_id)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {r.subscribers.length === 0 && <div className="rs-empty-hint">Nobody is on this report yet.</div>}

          {r.log.length > 0 && (
            <>
              <div className="rs-section-label">Recent activity</div>
              <div className="rs-log">
                {r.log.map((l, i) => (
                  <div key={i} className="rs-log-row">
                    <span className="rs-muted">{fmtDate(l.at)}</span>
                    <span>
                      <b>{l.by_name || "someone"}</b> {ACTION_LABEL[l.action] || l.action}{" "}
                      {SHOW_EMAIL.includes(l.action) && <span className="rs-mono">{l.email_id}</span>}
                      {l.note && <span className="rs-muted"> - {l.note}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ create modal
const CreateModal = ({ cat, cats, onClose, reload }) => {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [desc, setDesc] = useState("");
  const [cadence, setCadence] = useState("");
  const [job, setJob] = useState("");
  const [category, setCategory] = useState("");
  const [vis, setVis] = useState("open");
  const [subMe, setSubMe] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const finalCode = codeTouched ? code : slug(name);
  const similar = useMemo(() => {
    const t = tokens(name + " " + finalCode);
    if (!t.length) return [];
    return cat.filter((r) => tokens(r.report_code + " " + r.report_name).some((x) => t.includes(x))).slice(0, 5);
  }, [name, finalCode, cat]);

  const save = () => {
    setErr("");
    setSaving(true);
    api("create_report", { report_code: finalCode, report_name: name.trim(), description: desc, cadence, sender_job: job, visibility: vis, category: category || null, subscribe_me: subMe })
      .then((res) => {
        if (res.status === "success") {
          setDone(res);
          reload();
        } else setErr(res.error_message || "Could not create");
      })
      .catch((e) => setErr(e?.message))
      .finally(() => setSaving(false));
  };

  return (
    <div className="rs-modal-overlay" onClick={onClose}>
      <div className="rs-modal" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <>
            <div className="rs-modal-title">Report created</div>
            <div className="rs-modal-sub">{done.message} Put this in the script that sends it - the same query works on both databases:</div>
            <div className="rs-snippet-label">Aurora (order66)</div>
            <div className="rs-snippet-row rs-snippet-big">
              <code>{done.snippet || snippetFor(finalCode)}</code>
            </div>
            <div className="rs-snippet-label">Redshift (more-dw)</div>
            <div className="rs-snippet-row rs-snippet-big">
              <code>{done.snippet || snippetFor(finalCode)}</code>
            </div>
            <div className="rs-info-callout">
              Every subscribe or unsubscribe is written to Aurora and to Redshift at the same moment, so a script on either
              database picks up the change on its next run. The script decides the content and the schedule; this module only
              decides who receives it - subscribers can join or leave from here on their own.
            </div>
            <div className="rs-modal-actions">
              <button className="rs-btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rs-modal-title">New report</div>
            <div className="rs-modal-sub">Register a report so people can subscribe to it. You become its owner.</div>
            <div className="rs-field">
              <label className="rs-field-label">Report name</label>
              <input className="rs-field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. F&V Fill Rate Trends" autoFocus />
            </div>
            <div className="rs-field">
              <label className="rs-field-label">Report code - used by the script, cannot change later</label>
              <input
                className="rs-field-input rs-mono"
                value={finalCode}
                onChange={(e) => {
                  setCodeTouched(true);
                  setCode(slug(e.target.value));
                }}
                placeholder="fnv_fill_rate"
              />
              {similar.length > 0 && (
                <div className="rs-similar">
                  Similar codes already exist - reuse one instead of adding a near-duplicate:{" "}
                  {similar.map((s) => (
                    <span key={s.report_code} className="rs-mono rs-similar-code">
                      {s.report_code}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="rs-field">
              <label className="rs-field-label">What is in it</label>
              <textarea className="rs-field-input" rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="One or two lines a subscriber would want to know" />
            </div>
            <div className="rs-field-row">
              <div className="rs-field">
                <label className="rs-field-label">Cadence</label>
                <input className="rs-field-input" value={cadence} onChange={(e) => setCadence(e.target.value)} placeholder="daily 11:00 / Mon Wed Fri" />
              </div>
              <div className="rs-field">
                <label className="rs-field-label">Sending job (optional)</label>
                <input className="rs-field-input rs-mono" value={job} onChange={(e) => setJob(e.target.value)} placeholder="Glue / airflow job name" />
              </div>
            </div>
            <div className="rs-field">
              <label className="rs-field-label">Category</label>
              <CatSelect cats={cats} value={category} onChange={setCategory} />
            </div>
            <div className="rs-field">
              <label className="rs-field-label">Who can subscribe</label>
              <div className="rs-radio-row">
                <label className={`rs-radio ${vis === "open" ? "rs-radio-on" : ""}`}>
                  <input type="radio" checked={vis === "open"} onChange={() => setVis("open")} /> Anyone at More
                </label>
                <label className={`rs-radio ${vis === "restricted" ? "rs-radio-on" : ""}`}>
                  <input type="radio" checked={vis === "restricted"} onChange={() => setVis("restricted")} /> Only people I add
                </label>
              </div>
            </div>
            <label className="rs-check">
              <input type="checkbox" checked={subMe} onChange={(e) => setSubMe(e.target.checked)} /> Subscribe me as well
            </label>
            {err && <div className="rs-error">{err}</div>}
            <div className="rs-modal-actions">
              <button className="rs-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="rs-btn-primary" disabled={saving || name.trim().length < 3 || finalCode.length < 3} onClick={save}>
                {saving ? "Creating..." : "Create report"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ edit modal
const EditModal = ({ r, cats, onClose, say, reload }) => {
  const [f, setF] = useState({
    report_name: r.report_name,
    description: r.description || "",
    cadence: r.cadence || "",
    sender_job: r.sender_job || "",
    visibility: r.visibility,
    category: r.category || "",
    active: r.active,
  });
  const [handQ, setHandQ] = useState("");
  const [newOwner, setNewOwner] = useState(null);
  const [people] = usePeopleSearch(handQ, !newOwner);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = () => {
    setErr("");
    setSaving(true);
    api("update_report", { report_code: r.report_code, ...f, ...(newOwner ? { new_owner_employee_id: newOwner.employee_id } : {}) })
      .then((res) => {
        if (res.status === "success") {
          onClose();
          say(true, res.message);
          reload();
        } else setErr(res.error_message || "Could not save");
      })
      .catch((e) => setErr(e?.message))
      .finally(() => setSaving(false));
  };

  return (
    <div className="rs-modal-overlay" onClick={onClose}>
      <div className="rs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rs-modal-title">
          Edit <span className="rs-mono">{r.report_code}</span>
        </div>
        <div className="rs-modal-sub">The code stays as it is - scripts depend on it.</div>
        <div className="rs-field">
          <label className="rs-field-label">Report name</label>
          <input className="rs-field-input" value={f.report_name} onChange={set("report_name")} />
        </div>
        <div className="rs-field">
          <label className="rs-field-label">What is in it</label>
          <textarea className="rs-field-input" rows={2} value={f.description} onChange={set("description")} />
        </div>
        <div className="rs-field-row">
          <div className="rs-field">
            <label className="rs-field-label">Cadence</label>
            <input className="rs-field-input" value={f.cadence} onChange={set("cadence")} />
          </div>
          <div className="rs-field">
            <label className="rs-field-label">Sending job</label>
            <input className="rs-field-input rs-mono" value={f.sender_job} onChange={set("sender_job")} />
          </div>
        </div>
        <div className="rs-field">
          <label className="rs-field-label">Category</label>
          <CatSelect cats={cats} value={f.category} onChange={(v) => setF({ ...f, category: v })} />
        </div>
        <div className="rs-field">
          <label className="rs-field-label">Who can subscribe</label>
          <div className="rs-radio-row">
            <label className={`rs-radio ${f.visibility === "open" ? "rs-radio-on" : ""}`}>
              <input type="radio" checked={f.visibility === "open"} onChange={() => setF({ ...f, visibility: "open" })} /> Anyone at More
            </label>
            <label className={`rs-radio ${f.visibility === "restricted" ? "rs-radio-on" : ""}`}>
              <input type="radio" checked={f.visibility === "restricted"} onChange={() => setF({ ...f, visibility: "restricted" })} /> Only people I add
            </label>
          </div>
        </div>
        <label className="rs-check">
          <input type="checkbox" checked={f.active} onChange={set("active")} /> Report is live - untick when the script is retired; nobody can subscribe to a
          switched-off report
        </label>
        {!r.active && r.inactive_reason && (
          <div className="rs-info-callout">
            {r.inactive_reason}. Reports are switched off automatically when no sending job has read their recipient list for 14 days. If the
            script is running again, tick the box above - it also switches back on by itself the next time a run is seen.
          </div>
        )}
        <div className="rs-field rs-handover">
          <label className="rs-field-label">Hand over to someone else</label>
          {newOwner ? (
            <div className="rs-field-input rs-picked" onClick={() => setNewOwner(null)}>
              {newOwner.full_name} <span className="rs-muted">{newOwner.email_id}</span> <span className="rs-link-muted">change</span>
            </div>
          ) : (
            <>
              <input className="rs-field-input" placeholder="Search by name" value={handQ} onChange={(e) => setHandQ(e.target.value)} />
              <PeopleDrop people={people} onPick={setNewOwner} />
            </>
          )}
        </div>
        {err && <div className="rs-error">{err}</div>}
        <div className="rs-modal-actions">
          <button className="rs-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="rs-btn-primary" disabled={saving} onClick={save}>
            {saving ? "Saving..." : newOwner ? "Save and hand over" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ page
const Subscriptions = () => {
  const [tab, setTab] = useState("mine");
  const [me, setMe] = useState(null);
  const [cat, setCat] = useState([]);
  const [cats, setCats] = useState([]);
  const [owned, setOwned] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const say = (ok, text) => {
    setNotice({ ok, text });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadCatalogue = () =>
    api("catalogue")
      .then((r) => {
        if (r.status === "success") {
          setMe(r.me);
          setCat(r.data || []);
          setCats(r.categories || []);
        } else say(false, r.error_message || "Could not load reports");
      })
      .catch((e) => say(false, e?.message || "Could not load reports"))
      .finally(() => setLoaded(true));
  const loadOwned = () =>
    api("owned")
      .then((r) => r.status === "success" && setOwned(r.data || []))
      .catch(console.error);
  const reload = () => Promise.all([loadCatalogue(), loadOwned()]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = search.trim().toLowerCase();
  const matches = (r) =>
    inCat(r, catFilter) &&
    (!q ||
      r.report_code.toLowerCase().includes(q) ||
      (r.report_name || "").toLowerCase().includes(q) ||
      (r.owner_name || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q));
  const mine = cat.filter((r) => r.subscribed);
  const mineVisible = mine.filter(matches);
  const minePage = mineVisible.slice((page - 1) * PAGE, page * PAGE);
  const visible = cat.filter(matches);
  const pageRows = visible.slice((page - 1) * PAGE, page * PAGE);
  const changeSearch = (v) => {
    setSearch(v);
    setPage(1);
  };
  const changeCat = (c) => {
    setCatFilter(c);
    setPage(1);
  };
  const changeTab = (t) => {
    setTab(t);
    setCatFilter("");
    setPage(1);
  };

  const act = (path, body, after) => {
    if (busy) return;
    setBusy(body.report_code);
    api(path, body)
      .then((res) => {
        say(res.status === "success", res.message || res.error_message || "Could not update");
        if (res.status === "success") after();
      })
      .catch((e) => say(false, e?.message))
      .finally(() => setBusy(""));
  };
  const toggle = (r, subscribe) => act("toggle", { report_code: r.report_code, subscribe }, loadCatalogue);
  const claim = (r) => act("claim", { report_code: r.report_code }, reload);

  const unsubscribeBtn = (r) => (
    <button className="rs-link-danger" disabled={busy === r.report_code} onClick={() => toggle(r, false)}>
      {busy === r.report_code ? "Working..." : "Unsubscribe"}
    </button>
  );
  const tableAction = (r) =>
    r.subscribed ? (
      unsubscribeBtn(r)
    ) : !r.active ? null : r.visibility === "restricted" ? (
      <span className="rs-ask-owner">ask {r.owner_name ? r.owner_name.split(" ")[0] : "the owner"} to add you</span>
    ) : (
      <button className="rs-link-brand" disabled={busy === r.report_code} onClick={() => toggle(r, true)}>
        {busy === r.report_code ? "Working..." : "Subscribe"}
      </button>
    );

  return (
    <div className="rs-page">
      <div className="rs-page-head">
        <div>
          <div className="rs-page-title">Report Subscriptions</div>
          <div className="rs-page-sub">E-mail reports that come to {me?.email || "your company e-mail"}</div>
        </div>
        {tab === "mine" && (
          <button className="rs-btn-primary" onClick={() => setAddOpen(true)}>
            + Add subscription
          </button>
        )}
        {tab === "owned" && (
          <button className="rs-btn-primary" onClick={() => setCreateOpen(true)}>
            + New report
          </button>
        )}
      </div>

      {notice && (
        <div className={notice.ok ? "rs-notice" : "rs-error"} onClick={() => setNotice(null)}>
          {notice.text}
        </div>
      )}

      <div className="rs-tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`rs-tab ${tab === t.id ? "rs-tab-on" : ""}`} onClick={() => changeTab(t.id)}>
            {t.label}
            <span className="rs-tab-count">{t.id === "mine" ? mine.length : t.id === "owned" ? owned.length : cat.length}</span>
          </button>
        ))}
        <input className="rs-search" placeholder="Search report or code" value={search} onChange={(e) => changeSearch(e.target.value)} />
      </div>

      {tab === "mine" && (
        <>
          <CatChips rows={mine} value={catFilter} onChange={changeCat} />
          {minePage.map((r) => (
            <ReportCard key={r.report_code} r={r} action={unsubscribeBtn(r)} pill={false} />
          ))}
          {loaded && mine.length === 0 && (
            <div className="rs-empty-hint">
              You are not on any report mailer. Use <b>+ Add subscription</b> to pick one.
            </div>
          )}
          {loaded && mine.length > 0 && mineVisible.length === 0 && <div className="rs-empty-hint">None of your subscriptions match.</div>}
          <Pager total={mineVisible.length} page={page} onPage={setPage} />
          {mine.length > 0 && (
            <div className="rs-empty-hint">Unsubscribing stops the next run. A report owner cannot add you back - only you can, from here.</div>
          )}
        </>
      )}

      {tab === "owned" && (
        <>
          <div className="rs-page-sub rs-owned-intro">
            Each report has a code that the sending script uses to pick its recipients. You add or remove people here, including addresses
            outside More.
          </div>
          <CatChips rows={owned} value={catFilter} onChange={changeCat} />
          {owned.filter(matches).map((r) => (
            <OwnedCard
              key={r.report_code}
              r={r}
              open={expanded === r.report_code}
              onToggle={() => setExpanded(expanded === r.report_code ? null : r.report_code)}
              onEdit={setEditing}
              say={say}
              reload={reload}
            />
          ))}
          {loaded && owned.length === 0 && (
            <div className="rs-empty-hint">
              You do not own a report yet. Create one, or claim an unowned report under <b>All reports</b>.
            </div>
          )}
        </>
      )}

      {tab === "all" && (
        <>
          <CatChips rows={cat} value={catFilter} onChange={changeCat} />
          <div className="rs-table-wrap">
            <table className="rs-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Report</th>
                  <th>Owner</th>
                  <th>Access</th>
                  <th className="rs-num">Subscribers</th>
                  <th>Last sent</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr key={r.report_code} className={r.active ? "" : "rs-row-off"}>
                    <td>
                      <span className="rs-mono rs-code-cell">{r.report_code}</span>
                    </td>
                    <td>
                      <div className="rs-table-name">
                        {r.report_name} <CatTag c={r.category} />
                      </div>
                      {r.description && <div className="rs-table-desc">{r.description}</div>}
                      {!r.active && r.inactive_reason && <div className="rs-table-desc rs-warn-text">{r.inactive_reason}</div>}
                    </td>
                    <td>
                      {r.owner_name || (
                        <button className="rs-link-muted" disabled={busy === r.report_code} onClick={() => claim(r)}>
                          {busy === r.report_code ? "..." : "claim"}
                        </button>
                      )}
                    </td>
                    <td>
                      <Pill r={r} />
                    </td>
                    <td className="rs-num">{r.subscribers}</td>
                    <td className="rs-muted">{r.last_hit_at ? fmtDate(r.last_hit_at) : "-"}</td>
                    <td className="rs-action-cell">{tableAction(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loaded && visible.length === 0 && <div className="rs-empty-hint">No report matches "{search}".</div>}
          </div>
          <Pager total={visible.length} page={page} onPage={setPage} />
        </>
      )}

      {addOpen && <AddModal cat={cat} busy={busy} onSubscribe={(r) => toggle(r, true)} onClose={() => setAddOpen(false)} />}
      {createOpen && <CreateModal cat={cat} cats={cats} onClose={() => setCreateOpen(false)} reload={reload} />}
      {editing && <EditModal r={editing} cats={cats} onClose={() => setEditing(null)} say={say} reload={reload} />}
    </div>
  );
};
export default Subscriptions;

